/**
 * `SCR-HOME` — the wardrobe (11 states). FLOWS_SPEC §3.3.
 * OWNER: web-frontend-engineer.
 *
 * States the client's name, then THE NAME OF THE MASTER TAILOR WHO HOLDS HIS
 * RECORD ([T-16]). No greeting formula, no "Welcome back", no salutation.
 *
 * ZERO PROMOTIONAL CONTENT: no recommendation, no cloth suggestion, no
 * completeness meter, no gap badge, no anniversary, no "time for a new one".
 * And NO "COMMISSION AGAIN" — reorder is staged past Round 1 (D-029), and
 * shipping the affordance without the flow is a promise without a mechanism.
 */

import { useEffect } from 'react';
import { t } from '@pt/copy';
import { colourStatementInputs } from '@pt/fabric';
import { api } from '../lib/api.ts';
import { useResource } from '../lib/resource.ts';
import type { WardrobeEntry } from '../lib/contracts.ts';
import { dateInWords } from '@pt/view';
import { AppShell } from '../app/AppShell.tsx';
import { useNavigation } from '../app/Router.tsx';
import { useHouse } from '../app/SessionProvider.tsx';
import { ClothImage } from '../components/cloth.tsx';
import { Button, FactLine, Link, Statement } from '../components/primitives.tsx';
import { FetchError, LoadingRegion, OfflineStatement } from '../components/states.tsx';
import { stageLabel } from '../components/order.tsx';

/** The cloth as a swatch at scale on a neutral surround. An absent capture
 *  states that it is not yet made — never a broken image, never a grey box.
 *  `ClothImage`, not a bare `<img>`: a photographed cloth is served by
 *  reference and only its crop is the cloth (D-072). */
function WardrobeSwatch({ clothId }: { clothId: string }) {
  const capture = colourStatementInputs(clothId);
  if (capture?.macroSwatchPath == null) {
    return <div className="pt-card__missing">{t('book.imageMissing')}</div>;
  }
  return (
    <ClothImage clothId={clothId} src={capture.macroSwatchPath} className="pt-card__image" alt="" />
  );
}

/**
 * The wardrobe entry's second line. 5 September 2026 (finding C10): a suit
 * whose commission recorded no occasion printed " · delivered 3 March 2026" —
 * the one template with its first slot empty, a fragment that opens on a dot.
 * The line with no occasion is its own sentence.
 */
export function wardrobeDetailLine(
  suit: Pick<WardrobeEntry, 'occasionLabel' | 'deliveredAt' | 'placedAt'>,
): string {
  const deliveredDate = dateInWords(suit.deliveredAt ?? suit.placedAt);
  const occasionLabel = (suit.occasionLabel ?? '').trim();
  return occasionLabel === ''
    ? t('home.wardrobe.entryDetail.noOccasion', { deliveredDate })
    : t('home.wardrobe.entryDetail', { occasionLabel, deliveredDate });
}

export function HomeScreen() {
  const { navigate } = useNavigation();
  const { remember } = useHouse();
  const home = useResource(() => api.home(), []);

  useEffect(() => {
    if (home.data !== null) {
      remember({ tailor: home.data.tailor, contact: home.data.contact ?? null });
    }
  }, [home.data, remember]);

  const title = home.data === null ? t('global.nav.wardrobe') : t('home.heading', { clientName: home.data.client.displayName });

  return (
    <AppShell title={title} ground="book" banner={home.phase === 'offline' ? <OfflineStatement /> : null}>
      {home.phase === 'skeleton' || home.phase === 'idle' ? (
        <LoadingRegion rows={5} variant="block" slow={home.slow} label={t('global.nav.wardrobe')} />
      ) : null}

      {home.phase === 'error' ? <FetchError whatKey="home.error.what" onRetry={home.reload} /> : null}

      {home.data === null ? null : (
        <>
          {home.data.tailor === null ? null : (
            <FactLine>
              {t('home.tailorLine', {
                tailorName: home.data.tailor.displayName,
                tailorRole: t('global.role.master_tailor'),
              })}
            </FactLine>
          )}

          {/* ST-HOME-NORECORD — a quiet statement and the appointment route.
              NEVER an invitation to enter numbers. */}
          {home.data.hasCurrentRecord ? null : (
            <section className="pt-section">
              <Statement>{t('home.norecord.statement')}</Statement>
              <Statement>{t('home.norecord.commissionBlocked')}</Statement>
              <div className="pt-actions">
                <Button onClick={() => navigate('/measurements/remeasure')}>
                  {t('home.norecord.action')}
                </Button>
              </div>
            </section>
          )}

          {/* ST-HOME-DRAFT / ST-HOME-DRAFT-STALE — the FIRST region on the page. */}
          {home.data.draft === null ? null : (
            <section className="pt-section" aria-label={t('home.draft.heading')}>
              <h2 className="pt-cutsheet__blockTitle">{t('home.draft.heading')}</h2>
              <Statement>
                {home.data.draft.stale === false
                  ? t('home.draft.statement', {
                      lastTouchedDate: dateInWords(home.data.draft.lastTouchedAt),
                    })
                  : home.data.draft.staleReason === 'cloth'
                    ? t('home.draftStale.cloth', {
                        lastTouchedDate: dateInWords(home.data.draft.lastTouchedAt),
                        clothName: home.data.draft.clothName ?? '',
                      })
                    : t('home.draftStale.rules', {
                        lastTouchedDate: dateInWords(home.data.draft.lastTouchedAt),
                      })}
              </Statement>
              {/* Deleting takes the same number of actions as creating (B24). */}
              <div className="pt-actions pt-actions--equal">
                <Button onClick={() => navigate('/commission/make')}>
                  {t('home.draft.continue')}
                </Button>
                <Button
                  onClick={() => {
                    const draftId = home.data?.draft?.draftId;
                    if (draftId === undefined) return;
                    void api.deleteDraft(draftId).then(() => home.reload());
                  }}
                >
                  {t('home.draft.delete')}
                </Button>
              </div>
            </section>
          )}

          {/* The wardrobe — suits owned, by delivery date, newest first. */}
          <section className="pt-section" aria-label={t('home.wardrobe.heading')}>
            <h2 className="pt-cutsheet__blockTitle">{t('home.wardrobe.heading')}</h2>
            {home.data.wardrobe.length === 0 ? (
              home.data.orders.length === 0 ? (
                <Statement>{t('home.firsttime.statement', {
                  tailorName: home.data.tailor?.displayName ?? '',
                })}</Statement>
              ) : (
                <Statement>{t('home.ordersonly.wardrobeEmpty')}</Statement>
              )
            ) : (
              <ul className="pt-grid">
                {home.data.wardrobe.map((suit) => (
                  <li key={suit.orderRef}>
                    {/* The cloth as a swatch at scale on a neutral surround. */}
                    <WardrobeSwatch clothId={suit.clothId} />
                    <p className="pt-card__name">
                      {/* The server resolves the type and the occasion to
                          LABELS. Resolving them again here would be a second
                          vocabulary; the payload's words are the ones shown. */}
                      {t('home.wardrobe.entry', {
                        commissionTypeLabel: suit.commissionTypeLabel,
                        clothName: suit.clothName,
                      })}
                    </p>
                    <FactLine>{wardrobeDetailLine(suit)}</FactLine>
                    <FactLine>
                      <Link href={`/orders/${suit.orderRef}`} onNavigate={navigate}>
                        {suit.orderRef}
                      </Link>
                    </FactLine>
                    {/* NO "Commission again" here. D-029: no affordance without its flow. */}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Orders in progress. */}
          {home.data.orders.length === 0 ? null : (
            <section className="pt-section" aria-label={t('home.orders.heading')}>
              <h2 className="pt-cutsheet__blockTitle">{t('home.orders.heading')}</h2>
              <ul>
                {home.data.orders.map((order) => (
                  <li key={order.orderRef} className="pt-row">
                    <Link href={`/orders/${order.orderRef}`} onNavigate={navigate}>
                      {t('home.orders.entry', {
                        orderRef: order.orderRef,
                        commissionTypeLabel: order.commissionTypeLabel,
                        clothName: order.clothName,
                        stageLabel: stageLabel(order.state),
                      })}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* ST-HOME-FIRSTTIME's route out is a ROOM, not an offer. */}
          {home.data.wardrobe.length === 0 && home.data.orders.length === 0 ? (
            <FactLine>
              <Link href="/book" onNavigate={navigate}>
                {t('home.firsttime.bookLink')}
              </Link>
            </FactLine>
          ) : null}

          {/* ST-HOME-PARTIAL — a per-region error, never a whole-page error. */}
          {(home.data.regionErrors ?? []).map((region) => (
            <Statement key={region}>{t('home.partial.regionError')}</Statement>
          ))}
        </>
      )}
    </AppShell>
  );
}
